import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

import { formatNumber } from "../../../../../infrastructure/utils/formatters";
import { pick } from "./managementDashboardUtils";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

export const TREND_METRIC_GROUPS = {
  requests: {
    label: "درخواست‌ها",
    codes: [
      "NewApplications",
      "InitialApprovals",
      "CustomerContracts",
      "EnteredOperations",
      "CommissionedProjects",
    ],
  },
  capacity: {
    label: "ظرفیت",
    codes: ["AddedPipelineCapacityKw", "CommissionedCapacityKw"],
  },
  financial: {
    label: "مالی",
    codes: ["ApprovedCustomerContractValue"],
  },
};

const ManagementTrendChart = ({ series }) => {
  const buckets = pick(series, "buckets", "Buckets") || [];
  const valueType = pick(series, "valueType", "ValueType");
  const unit = pick(series, "unit", "Unit") || "";

  const chartData = useMemo(
    () => ({
      labels: buckets.map((b) => pick(b, "labelFa", "LabelFa") || ""),
      datasets: [
        {
          label: pick(series, "title", "Title"),
          data: buckets.map((b) => Number(pick(b, "value", "Value") ?? 0)),
          borderColor: "#1565C0",
          backgroundColor: "rgba(21, 101, 192, 0.12)",
          tension: 0.2,
          fill: true,
          pointRadius: 2,
        },
      ],
    }),
    [buckets, series]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const val = ctx.parsed.y;
              if (valueType === 3 || unit === "ریال") return `${formatNumber(val)} ریال`;
              if (valueType === 2 || unit?.toLowerCase() === "kw") return `${formatNumber(val)} kW`;
              return `${formatNumber(val)} ${unit}`.trim();
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (v) => formatNumber(v),
          },
        },
      },
    }),
    [unit, valueType]
  );

  return (
    <Box sx={{ height: 280 }}>
      <Line data={chartData} options={options} />
    </Box>
  );
};

const ManagementTrendMetricSelector = ({ group, seriesList, selectedCode, onChange }) => {
  const options = seriesList.filter((s) => {
    const code = pick(s, "code", "Code");
    return TREND_METRIC_GROUPS[group]?.codes.includes(code);
  });

  return (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel>شاخص</InputLabel>
      <Select
        label="شاخص"
        value={selectedCode || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((s) => {
          const code = pick(s, "code", "Code");
          const title = pick(s, "title", "Title");
          const available = pick(s, "isAvailable", "IsAvailable") !== false;
          return (
            <MenuItem key={code} value={code} disabled={!available}>
              {title}
              {!available ? " (ناموجود)" : ""}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const ManagementTrendSection = ({
  loading = false,
  error = null,
  seriesList = [],
  metricGroup = "requests",
  selectedMetricCode = "",
  onMetricGroupChange,
  onMetricChange,
  onRetry,
}) => {
  const activeSeries =
    seriesList.find((s) => pick(s, "code", "Code") === selectedMetricCode)
    || seriesList.find((s) =>
      TREND_METRIC_GROUPS[metricGroup]?.codes.includes(pick(s, "code", "Code"))
    );

  const isAvailable = pick(activeSeries, "isAvailable", "IsAvailable") !== false;
  const unavailableReason = pick(activeSeries, "unavailableReason", "UnavailableReason");

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          روند شاخص‌ها
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>گروه</InputLabel>
            <Select
              label="گروه"
              value={metricGroup}
              onChange={(e) => onMetricGroupChange(e.target.value)}
            >
              {Object.entries(TREND_METRIC_GROUPS).map(([key, meta]) => (
                <MenuItem key={key} value={key}>
                  {meta.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ManagementTrendMetricSelector
            group={metricGroup}
            seriesList={seriesList}
            selectedCode={selectedMetricCode}
            onChange={onMetricChange}
          />
        </Box>

        {loading ? (
          <Skeleton variant="rounded" height={280} />
        ) : error ? (
          <Box>
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Button size="small" variant="outlined" onClick={onRetry}>
              تلاش مجدد
            </Button>
          </Box>
        ) : !activeSeries ? (
          <Typography variant="body2" color="text.secondary">
            سری داده‌ای برای نمایش وجود ندارد.
          </Typography>
        ) : !isAvailable ? (
          <Typography variant="body2" color="warning.dark">
            {unavailableReason || "این سری در حال حاضر قابل نمایش نیست."}
          </Typography>
        ) : (
          <ManagementTrendChart series={activeSeries} />
        )}
      </CardContent>
    </Card>
  );
};

export default ManagementTrendSection;
